import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import {
  PAGAMENTOS_LIGA,
  type PagamentosLigaData,
  type PagamentosLigaVars,
} from '../../../services/pagamentoInscricaoService';
import { StatusPagamento } from '../../../types/pagamento';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import { IsentoBadge } from '../../../components/financeiro';
import { formatCurrency } from '../../../utils/money';

const COLUMN_COUNT = 8;

const STATUS_OPTIONS: { label: string; value: StatusPagamento | null }[] = [
  { label: 'Todos', value: null },
  { label: 'Pago', value: 'PAGO' as StatusPagamento },
  { label: 'Pendente', value: 'PENDENTE' as StatusPagamento },
  { label: 'Cancelado', value: 'CANCELADO' as StatusPagamento },
  { label: 'Estornado', value: 'ESTORNADO' as StatusPagamento },
  { label: 'Gratuito', value: 'GRATUITO' as StatusPagamento },
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface LigaRecebimentosTabProps {
  ligaId: string;
}

const LigaRecebimentosTab: React.FC<LigaRecebimentosTabProps> = ({ ligaId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<StatusPagamento | null>(null);
  const [torneioFilter, setTorneioFilter] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);

  const { data, loading, error } = useQuery<PagamentosLigaData, PagamentosLigaVars>(
    PAGAMENTOS_LIGA,
    {
      variables: { ligaId, size: 9999 },
      skip: !ligaId,
      fetchPolicy: 'no-cache',
    },
  );

// A aba Recebimentos excluía `GRATUITO` da listagem desde 2026-03-10 (commit
// 0b3d085), sob a regra "recebimentos é dinheiro que entrou" — regra que a PRD
// `correcoes-reuniao-05082026` avaliou e manteve. O owner a reverteu em QA
// (2026-09-01), pela alternativa que a própria PRD havia listado e descartado:
// incluir o gratuito, porém MARCADO como isento (ver `IsentoBadge`), para que
// uma linha de R$ 0,00 não se confunda com valor recebido.
  const allItems = useMemo(
    () => data?.pagamentosInscricaoPorLiga?.items ?? [],
    [data],
  );

  const torneioOptions = useMemo(() => {
    const map = new Map<string, string>();
    allItems.forEach((p) => {
      p.inscricoes?.forEach((ins) => {
        const torneio = ins.categoriaTorneio?.torneio;
        if (torneio?.id && torneio.nome) {
          map.set(torneio.id, torneio.nome);
        }
      });
    });
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [allItems]);

  const hasDateError = !!(
    dataInicio &&
    dataFim &&
    dayjs(dataInicio).isAfter(dayjs(dataFim))
  );

  const filteredItems = useMemo(() => {
    let items = allItems;

    if (statusFilter) {
      items = items.filter((p) => p.status === statusFilter);
    }

    if (torneioFilter) {
      items = items.filter((p) =>
        p.inscricoes?.some(
          (ins) => ins.categoriaTorneio?.torneio?.id === torneioFilter,
        ),
      );
    }

    if (dataInicio && !hasDateError) {
      const start = dayjs(dataInicio).startOf('day');
      items = items.filter((p) => {
        const date = p.dataPagamento || p.criadoEm;
        return date ? dayjs(date).isAfter(start) || dayjs(date).isSame(start) : false;
      });
    }

    if (dataFim && !hasDateError) {
      const end = dayjs(dataFim).endOf('day');
      items = items.filter((p) => {
        const date = p.dataPagamento || p.criadoEm;
        return date ? dayjs(date).isBefore(end) || dayjs(date).isSame(end) : false;
      });
    }

    return items;
  }, [allItems, statusFilter, torneioFilter, dataInicio, dataFim, hasDateError]);

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter((value || null) as StatusPagamento | null);
    setPage(0);
  };

  const handleTorneioChange = (value: string) => {
    setTorneioFilter(value || null);
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setTorneioFilter(null);
    setDataInicio(null);
    setDataFim(null);
    setPage(0);
  };

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  const renderCategoriaLinks = (
    inscricoes?: {
      categoriaTorneio?: {
        id: string;
        nome: string;
        torneio?: { id: string } | null;
      } | null;
    }[],
  ) => {
    if (!inscricoes || inscricoes.length === 0) return '-';
    const seen = new Map<string, { catId: string; catNome: string; torneioId: string }>();
    inscricoes.forEach((ins) => {
      const cat = ins.categoriaTorneio;
      if (cat?.id && cat.nome && cat.torneio?.id) {
        seen.set(cat.id, { catId: cat.id, catNome: cat.nome, torneioId: cat.torneio.id });
      }
    });
    const entries = Array.from(seen.values());
    if (entries.length === 0) return '-';
    return entries.map((e, i) => (
      <React.Fragment key={e.catId}>
        {i > 0 && ', '}
        <Link to={`/tournament/${e.torneioId}/category/${e.catId}`} style={linkStyle}>
          {e.catNome}
        </Link>
      </React.Fragment>
    ));
  };

  const renderUsuarioLinks = (
    inscricoes?: {
      equipe?: {
        atletas?: { id: string; usuario?: { nome: string } | null }[];
      } | null;
    }[],
  ) => {
    if (!inscricoes || inscricoes.length === 0) return '-';
    const seen = new Map<string, { atletaId: string; nome: string }>();
    inscricoes.forEach((ins) => {
      ins.equipe?.atletas?.forEach((a) => {
        if (a.usuario?.nome) {
          seen.set(a.id, { atletaId: a.id, nome: a.usuario.nome });
        }
      });
    });
    const entries = Array.from(seen.values());
    if (entries.length === 0) return '-';
    return entries.map((e, i) => (
      <React.Fragment key={e.atletaId}>
        {i > 0 && ', '}
        <Link to={`/userpage/${e.atletaId}`} style={linkStyle}>
          {e.nome}
        </Link>
      </React.Fragment>
    ));
  };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Torneio"
              size="small"
              fullWidth
              value={torneioFilter || ''}
              onChange={(e) => handleTorneioChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todos</MenuItem>
              {torneioOptions.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.nome}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataInicio || ''}
              onChange={(e) => {
                setDataInicio(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataFim || ''}
              onChange={(e) => {
                setDataFim(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
            />
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Usuário</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Torneio</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Meio Pagamento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Data</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar recebimentos. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhum recebimento encontrado.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{renderUsuarioLinks(p.inscricoes)}</TableCell>
                  <TableCell>
                    {p.inscricoes?.[0]?.categoriaTorneio?.torneio?.id ? (
                      <Link
                        to={`/tournamentsingle/${p.inscricoes[0].categoriaTorneio.torneio.id}`}
                        style={linkStyle}
                      >
                        {p.inscricoes[0].categoriaTorneio.torneio.nome}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{renderCategoriaLinks(p.inscricoes)}</TableCell>
                  <TableCell>{formatCurrency(p.valor ?? 0)}</TableCell>
                  <TableCell>
                    <PaymentStatusChip status={p.status} />
                    <IsentoBadge inscricoes={p.inscricoes} />
                  </TableCell>
                  <TableCell>{p.meioPagamento || '-'}</TableCell>
                  <TableCell>{formatDate(p.dataPagamento || p.criadoEm)}</TableCell>
                  <TableCell align="center">
                    {p.inscricoes?.[0]?.id && p.inscricoes[0].categoriaTorneio?.torneio?.id && (
                      <Link
                        to={`/liga/tournament/${p.inscricoes[0].categoriaTorneio.torneio.id}/inscricao/${p.inscricoes[0].id}/editar?categoriaId=${p.inscricoes[0].categoriaTorneio.id}&tab=1`}
                        style={linkStyle}
                        title="Editar inscrição"
                      >
                        <EditIcon fontSize="small" />
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>
    </>
  );
};

export default LigaRecebimentosTab;
